'use client'

import * as React from 'react'
import { cn } from '../helpers/index.js'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cva } from 'class-variance-authority'
import type { Align, Shape } from '../types/props.js'

type tabShape = Shape | 'default'

const TabListContext = React.createContext<{
  align?: Align
  shape: tabShape
}>({
  align: 'left',
  shape: 'square',
})

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
    align?: Align
    shape?: tabShape
  }
>(({ children, align = 'left', shape = 'default', ...props }, ref) => (
  <TabsPrimitive.Root ref={ref} {...props}>
    <TabListContext.Provider value={{ align, shape }}>{children}</TabListContext.Provider>
  </TabsPrimitive.Root>
))
Tabs.displayName = 'Tabs'

const tabsListVariants = cva('rounded-tabs', {
  variants: {
    align: {
      left: 'justify-left',
      center: 'justify-center',
      right: 'justify-end',
    },
    shape: {
      default: '',
      rounded: 'rounded-[28px]',
      square: 'rounded-none',
    },
  },
  defaultVariants: {
    align: 'left',
    shape: 'default',
  },
})

const tabsTriggerVariants = cva('rounded-tabs', {
  variants: {
    shape: {
      default: '',
      rounded: 'rounded-[28px]',
      square: 'rounded-none',
    },
  },
  defaultVariants: {
    shape: 'default',
  },
})
const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const { align: alignFromParent, shape: shapeFromParent } = React.useContext(TabListContext)
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        'bg-tabs-list-bg flex w-fit max-w-full overflow-x-auto p-1',
        tabsListVariants({
          align: alignFromParent,
          shape: shapeFromParent,
        }),
        className
      )}
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
      {...props}
    />
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, onClick, ...props }, ref) => {
  const { shape: shapeFromParent } = React.useContext(TabListContext)
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e)
    const tabElement = e?.currentTarget
    const container = e?.currentTarget?.parentElement
    if (tabElement && container) {
      const tabLeft = tabElement.offsetLeft
      const tabWidth = tabElement.offsetWidth
      const containerWidth = container.offsetWidth
      const scrollTo = tabLeft - containerWidth / 2 + tabWidth / 2
      container.scrollTo({
        left: scrollTo,
        behavior: 'smooth',
      })
    }
  }
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'text-info-primary lg-desktop:text-base disabled:text-btn-secondary-disabled lg-desktop:px-[28px] lg-desktop:pt-[15px] lg-desktop:pb-[14px] shrink-0 px-5 pb-2.5 pt-[11px] text-sm font-bold data-[state=active]:bg-white data-[state=active]:text-[#080A0F]',
        className,
        tabsTriggerVariants({
          shape: shapeFromParent,
        })
      )}
      onClick={handleClick}
      {...props}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => <TabsPrimitive.Content ref={ref} className={cn(className)} {...props} />)
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
